// src/calendario/CalendarioGlobal.jsx
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import "react-big-calendar/lib/css/react-big-calendar.css";

import {
  format,
  parse,
  startOfWeek as dfnsStartOfWeek,
  getDay,
  addMonths,
  subMonths,
} from "date-fns";
import esES from "date-fns/locale/es";

import { Global } from "../helpers/Global";

// ===== MUI =====
import {
  Box,
  Paper,
  Typography,
  Stack,
  TextField,
  Button,
  Chip,
  Autocomplete,
  FormControlLabel,
  Switch,
  Divider,
} from "@mui/material";

function normalize(text) {
  return (
    (text || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") || ""
  );
}

/* ========= Localizer correcto (firma startOfWeek con date) ========= */
const localizer = dateFnsLocalizer({
  format: (date, formatStr, options) =>
    format(date, formatStr, { ...(options || {}), locale: esES }),
  parse: (value, formatStr, options) =>
    parse(value, formatStr, new Date(), { ...(options || {}), locale: esES }),
  startOfWeek: (date, options) =>
    dfnsStartOfWeek(date, { weekStartsOn: 1, locale: esES, ...(options || {}) }),
  getDay,
  locales: { es: esES },
});

const CustomEvent = ({ event }) => (
  <div>
    <div style={{ fontWeight: 700 }}>
      {event.usuario ? `[${event.usuario}] ` : ""}
      {event.title}
    </div>
    {event.descripcion && (
      <div style={{ fontSize: "0.9em", fontWeight: 400 }}>{event.descripcion}</div>
    )}
  </div>
);

export const CalendarioGlobal = () => {
  const calendarRef = useRef();

  const [plantillas, setPlantillas] = useState([]);
  const [usuarios, setUsuarios] = useState([]);

  const [todosLosTurnos, setTodosLosTurnos] = useState([]);
  const [eventos, setEventos] = useState([]);

  const [vistaActual, setVistaActual] = useState("week");
  const [rangoVisible, setRangoVisible] = useState(null);
  const [rangoCargado, setRangoCargado] = useState(() => {
    const hoy = new Date();
    const desde = subMonths(new Date(hoy.getFullYear(), hoy.getMonth(), 1), 3);
    const hasta = addMonths(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0), 4);
    return { desde, hasta };
  });

  const [mensaje, setMensaje] = useState("");
  const [loading, setLoading] = useState(false);

  // ---- FILTROS ----
  const [filtroUsuarios, setFiltroUsuarios] = useState([]);      // ids (number)
  const [filtroPlantillas, setFiltroPlantillas] = useState([]);  // ids (number) y -1 para "sin plantilla"
  const [filtroTipos, setFiltroTipos] = useState(new Set(["turno", "ausencia"]));
  const [mostrarInactivos, setMostrarInactivos] = useState(true);
  const [busqueda, setBusqueda] = useState("");

  const usuariosById = useMemo(() => {
    const m = new Map();
    (usuarios || []).forEach((u) => {
      m.set(Number(u.id), `${u.nombre} ${u.apellidos || ""}`.trim());
    });
    return m;
  }, [usuarios]);

  const plantillasById = useMemo(() => {
    const m = new Map();
    (plantillas || []).forEach((p) => m.set(String(p.id), p));
    return m;
  }, [plantillas]);

  // ===== Carga plantillas + usuarios =====
  useEffect(() => {
    (async () => {
      try {
        const [resPlantillas, resUsuarios] = await Promise.all([
          fetch(`${Global.url}turnos/plantillas`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
          fetch(`${Global.url}usuarios`, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          }),
        ]);

        const dataPlantillas = resPlantillas.ok ? await resPlantillas.json() : [];
        const dataUsuarios = resUsuarios.ok ? await resUsuarios.json() : [];

        setPlantillas(Array.isArray(dataPlantillas) ? dataPlantillas : []);
        setUsuarios(Array.isArray(dataUsuarios) ? dataUsuarios : []);
      } catch (e) {
        console.error(e);
        setMensaje("Error al cargar plantillas o usuarios.");
      }
    })();
  }, []);

  const fetchTurnosGlobales = (desde, hasta) => {
    setLoading(true);
    setMensaje("");

    const ymdDesde = desde.toISOString().slice(0, 10);
    const ymdHasta = hasta.toISOString().slice(0, 10);

    fetch(`${Global.url}turnos/global?desde=${ymdDesde}&hasta=${ymdHasta}`, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        const turnos = (Array.isArray(data) ? data : []).map((turno) => {
          const usuarioNombre =
            usuariosById.get(Number(turno.usuario_id)) || `ID ${turno.usuario_id}`;

          return {
            id: turno.id,
            title: turno.titulo || (turno.tipo === "ausencia" ? "Ausencia" : "Turno"),
            start: new Date(String(turno.inicio).replace(" ", "T")),
            end: new Date(String(turno.fin).replace(" ", "T")),
            allDay: false,
            activo: turno.activo,
            tipo: turno.tipo,
            descripcion: turno.descripcion,
            plantilla_id: turno.plantilla_id ?? null,
            usuario: usuarioNombre,
            usuario_id: turno.usuario_id,
          };
        });

        setTodosLosTurnos(turnos);
        setRangoCargado({ desde, hasta });
      })
      .catch((e) => {
        console.error(e);
        setMensaje("Error al cargar turnos.");
      })
      .finally(() => setLoading(false));
  };

  // bootstrap visible + fetch inicial
  useEffect(() => {
    const hoy = new Date();
    const desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    const hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    hasta.setHours(23, 59, 59, 999);

    setRangoVisible({ desde, hasta });
    fetchTurnosGlobales(rangoCargado.desde, rangoCargado.hasta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usuariosById]);

  // Ajusta rango visible al cambiar a "day"
  useEffect(() => {
    if (vistaActual === "day") {
      const visibleDate = calendarRef.current?.state?.currentDate || new Date();
      const desde = new Date(visibleDate);
      const hasta = new Date(visibleDate);
      desde.setHours(0, 0, 0, 0);
      hasta.setHours(23, 59, 59, 999);
      setRangoVisible({ desde, hasta });
    }
  }, [vistaActual]);

  // ===== Opciones filtros =====
  const usuarioOptions = useMemo(() => {
    return (usuarios || [])
      .slice()
      .sort((a, b) =>
        `${a.nombre} ${a.apellidos || ""}`.localeCompare(`${b.nombre} ${b.apellidos || ""}`)
      )
      .map((u) => ({ id: Number(u.id), label: `${u.nombre} ${u.apellidos || ""}`.trim() }));
  }, [usuarios]);

  const plantillaOptions = useMemo(() => {
    const base = (plantillas || [])
      .slice()
      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
      .map((p) => ({ id: Number(p.id), label: p.nombre || `Plantilla ${p.id}` }));

    const haySinPlantilla = (todosLosTurnos || []).some((t) => !t.plantilla_id);
    return haySinPlantilla ? [{ id: -1, label: "Sin plantilla" }, ...base] : base;
  }, [plantillas, todosLosTurnos]);

  const selectedUsuarios = useMemo(
    () => usuarioOptions.filter((o) => filtroUsuarios.includes(o.id)),
    [usuarioOptions, filtroUsuarios]
  );

  const selectedPlantillas = useMemo(
    () => plantillaOptions.filter((o) => filtroPlantillas.includes(o.id)),
    [plantillaOptions, filtroPlantillas]
  );

  // ===== Filtrado =====
  useLayoutEffect(() => {
    if (!rangoVisible) return;

    const usuariosSel = new Set(filtroUsuarios);
    const plantillasSelRaw = new Set(filtroPlantillas);
    const plantillasSel = new Set([...plantillasSelRaw].map((v) => (v === -1 ? null : v)));
    const tiposSel = filtroTipos;
    const q = normalize(busqueda);

    const filtrados = (todosLosTurnos || []).filter((evt) => {
      if (evt.start > rangoVisible.hasta || evt.end < rangoVisible.desde) return false;

      if (!mostrarInactivos && !evt.activo) return false;

      if (usuariosSel.size > 0 && !usuariosSel.has(Number(evt.usuario_id))) return false;

      if (plantillasSel.size > 0 && !plantillasSel.has(evt.plantilla_id ?? null)) return false;

      if (!tiposSel.has(evt.tipo)) return false;

      if (q) {
        const hay =
          normalize(evt.title).includes(q) ||
          normalize(evt.descripcion).includes(q) ||
          normalize(evt.usuario).includes(q);
        if (!hay) return false;
      }

      return true;
    });

    setEventos(filtrados);
  }, [
    rangoVisible,
    todosLosTurnos,
    filtroUsuarios,
    filtroPlantillas,
    filtroTipos,
    mostrarInactivos,
    busqueda,
  ]);

  const handleRangeChange = (range) => {
    let desde, hasta;

    if (Array.isArray(range)) {
      desde = new Date(range[0]);
      hasta = new Date(range[range.length - 1]);
    } else if (range?.start && range?.end) {
      desde = new Date(range.start);
      hasta = new Date(range.end);
    } else if (range instanceof Date) {
      desde = new Date(range);
      hasta = new Date(range);
    } else {
      const hoy = new Date();
      desde = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      hasta = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
    }

    desde.setHours(0, 0, 0, 0);
    hasta.setHours(23, 59, 59, 999);
    setRangoVisible({ desde, hasta });

    if (desde < rangoCargado.desde || hasta > rangoCargado.hasta) {
      const nuevoCentro = new Date(desde.getFullYear(), desde.getMonth(), 1);
      const nuevoDesde = subMonths(nuevoCentro, 3);
      const nuevoHasta = addMonths(
        new Date(nuevoCentro.getFullYear(), nuevoCentro.getMonth() + 1, 0),
        4
      );
      fetchTurnosGlobales(nuevoDesde, nuevoHasta);
    }
  };

  const eventPropGetter = (event) => {
    const estilo = {};

    if (event.plantilla_id) {
      const plantilla = plantillasById.get(String(event.plantilla_id));
      if (plantilla?.color) {
        estilo.backgroundColor = plantilla.color;
        estilo.color = "#222";
      }
    }

    if (event.tipo === "ausencia") {
      estilo.backgroundColor = "#ffbaba";
      estilo.color = "#fff";
    }

    if (!event.activo) {
      estilo.backgroundColor = "#ddd";
      estilo.color = "#888";
      estilo.textDecoration = "line-through";
    }

    if (!estilo.backgroundColor) {
      estilo.backgroundColor = "#85C1E9";
      estilo.color = "#222";
    }

    estilo.borderRadius = 1;
    return { style: estilo };
  };

  const toggleTipo = (tipo) => {
    setFiltroTipos((prev) => {
      const next = new Set(prev);
      if (next.has(tipo)) next.delete(tipo);
      else next.add(tipo);
      return next;
    });
  };

  const limpiarFiltros = () => {
    setFiltroUsuarios([]);
    setFiltroPlantillas([]);
    setFiltroTipos(new Set(["turno", "ausencia"]));
    setMostrarInactivos(true);
    setBusqueda("");
  };

  const minTime = new Date(2000, 0, 1, 0, 0, 0, 0);
  const maxTime = new Date(2000, 0, 1, 23, 59, 59, 999);

  return (
    <Box className="appContainer appContainer--wide">
      <Stack spacing={2}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Vista global de turnos
        </Typography>

        {mensaje && (
          <Typography color="error" sx={{ fontWeight: 600 }}>
            {mensaje}
          </Typography>
        )}

        {/* =========================
            Filtros (1 línea en desktop)
           ========================= */}
        <Paper sx={{ p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap", // móvil -> varias filas
            }}
          >
            {/* Usuarios */}
            <Autocomplete
              multiple
              size="small"
              options={usuarioOptions}
              value={selectedUsuarios}
              onChange={(_, vals) => setFiltroUsuarios(vals.map((v) => Number(v.id)))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => Number(a.id) === Number(b.id)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip size="small" label={option.label} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Usuarios" placeholder="Todos" />}
              sx={{
                width: { xs: "100%", md: 360 },
                minWidth: { md: 280 },
              }}
              clearOnEscape
            />

            {/* Plantillas */}
            <Autocomplete
              multiple
              size="small"
              options={plantillaOptions}
              value={selectedPlantillas}
              onChange={(_, vals) => setFiltroPlantillas(vals.map((v) => Number(v.id)))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(a, b) => Number(a.id) === Number(b.id)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip size="small" label={option.label} {...getTagProps({ index })} />
                ))
              }
              renderInput={(params) => <TextField {...params} label="Plantillas" placeholder="Todas" />}
              sx={{
                width: { xs: "100%", md: 260 },
                minWidth: { md: 220 },
              }}
              clearOnEscape
            />

            {/* Búsqueda */}
            <TextField
              size="small"
              label="Búsqueda"
              placeholder="Título, descripción o usuario…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              sx={{
                width: { xs: "100%", md: 260 },
                minWidth: { md: 220 },
              }}
            />

            {/* Separador suave en desktop */}
            <Divider
              orientation="vertical"
              flexItem
              sx={{ display: { xs: "none", md: "block" }, mx: 0.5 }}
            />

            {/* Toggles */}
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={mostrarInactivos}
                  onChange={(e) => setMostrarInactivos(e.target.checked)}
                />
              }
              label="Inactivos"
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={filtroTipos.has("turno")}
                  onChange={() => toggleTipo("turno")}
                />
              }
              label="Turno"
              sx={{ m: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={filtroTipos.has("ausencia")}
                  onChange={() => toggleTipo("ausencia")}
                />
              }
              label="Ausencia"
              sx={{ m: 0 }}
            />

            {/* Limpiar a la derecha */}
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center" }}>
              <Button onClick={limpiarFiltros} variant="outlined" size="small">
                Limpiar
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* =========================
            Calendario
           ========================= */}
        <Paper
          sx={{
            p: 1,
            height: "78vh",
            overflow: "hidden",

            // Toolbar compacta
            "& .rbc-toolbar": {
              padding: "0px 6px",
              marginBottom: "6px",
              minHeight: 36,
            },
            "& .rbc-toolbar-label": {
              padding: 0,
              lineHeight: "36px",
              fontSize: 14,
            },
            "& .rbc-btn-group button": {
              padding: "4px 10px",
              lineHeight: 1.2,
              minHeight: 32,
            },
            "& .rbc-btn-group": {
              display: "inline-flex",
              alignItems: "center",
              gap: "2px",
            },
          }}
        >
          <Box sx={{ height: "100%" }}>
            <Calendar
              ref={calendarRef}
              localizer={localizer}
              events={eventos}
              startAccessor="start"
              endAccessor="end"
              titleAccessor="title"
              defaultView="week"
              views={["day", "week", "month", "agenda"]}
              onView={setVistaActual}
              onRangeChange={handleRangeChange}
              scrollToTime={new Date(2000, 0, 1, 7, 30)}
              min={minTime}
              max={maxTime}
              step={15}
              timeslots={4}
              popup
              selectable={false}
              style={{ height: "100%" }}
              eventPropGetter={eventPropGetter}
              components={{ event: CustomEvent }}
              messages={{
                today: "Hoy",
                previous: "Ant",
                next: "Sig",
                month: "Mes",
                week: "Semana",
                day: "Día",
                agenda: "Agenda",
                date: "Fecha",
                time: "Hora",
                event: "Evento",
                noEventsInRange: "No hay eventos en este rango.",
                showMore: (total) => `+ Ver ${total} más`,
              }}
            />
          </Box>
        </Paper>

        <Typography sx={{ color: "text.secondary" }}>
          {loading ? "Cargando turnos…" : `${eventos.length} turno(s) en vista.`}
        </Typography>
      </Stack>
    </Box>
  );
};
